import {
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Filter from "@saleor/components/Filter";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination/TablePagination";
import {
  createFilterStructureForPaymentCollection,
  createFilterStructureForToRemit,
  RemittanceRunnerFilterKeys,
  RemittanceRunnerFilterOpts
} from "@saleor/remittanceRunner/filters/filters";
import { useTableStyles } from "@saleor/remittanceRunner/styles/tableStyles";
import {
  RemittanceRunnerOrders_remittanceOrders_edges_node,
  RemittanceRunnerOrdersVariables
} from "@saleor/remittanceRunner/types/RemittanceRunnerOrders";
import { RemittanceRunnerListUrlQueryParams } from "@saleor/remittanceRunner/url";
import { FilterMenuProps, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SearchInput from "../../../components/SearchBar/SearchInput";
import TableBodyContent from "./TableBodyContent";

const useStyles = makeStyles(
  theme => ({
    container: {
      height: "50vh",
      paddingTop: "15px"
    },
    divFilterSearch: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexWrap: "wrap",
      padding: theme.spacing(1, 3)
    },
    tableOrders: {
      height: "85% !important"
    }
  }),
  { name: "RemittanceRunnerTableToRemit" }
);

const numberOfColumns = 7;
const dateFormat = "MMM DD, yyyy hh:mm A";

export interface RemittanceRunnerTableToRemitProps
  extends ListProps,
    FilterMenuProps<RemittanceRunnerFilterKeys, RemittanceRunnerFilterOpts> {
  params: RemittanceRunnerListUrlQueryParams;
  orderList: RemittanceRunnerOrders_remittanceOrders_edges_node[];
  loadingOrders: boolean;
  checkedOrders: any[];
  handleCheckedAll: (event) => void;
  handleChecked: (event, order_id) => void;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  refetch: (variables?: RemittanceRunnerOrdersVariables) => any;
  forRemittance: boolean;
  checkedAll: boolean;
}

const RemittanceRunnerTableToRemit: React.FC<RemittanceRunnerTableToRemitProps> = props => {
  const {
    params,
    orderList,
    loadingOrders,
    checkedOrders,
    checkedAll,
    settings,
    pageInfo,
    disabled,
    onFilterChange,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    handleCheckedAll,
    handleChecked,
    setSearchQuery,
    forRemittance,
    filterOpts,
    ...listProps
  } = props;
  const classes = useStyles(props);
  const tblClasses = useTableStyles(props);
  const currencySymbol = "PHP";
  const intl = useIntl();
  const filterMenu = forRemittance
    ? createFilterStructureForToRemit(intl, filterOpts)
    : createFilterStructureForPaymentCollection(intl, filterOpts);

  return (
    <Card className={classes.container}>
      <div className={classes.divFilterSearch}>
        <Filter
          currencySymbol={currencySymbol}
          menu={filterMenu}
          onFilterAdd={onFilterChange}
          sorted={false}
        />
        <SearchInput
          initialSearch={""}
          placeholder={"Search"}
          onSearchChange={searchText => {
            setSearchQuery(searchText);
          }}
        />
      </div>
      <div className={`${tblClasses.tableRoot} ${classes.tableOrders}`}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  key={"chkAll"}
                  checked={checkedAll}
                  onChange={ev => handleCheckedAll(ev)}
                />
              </TableCell>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage
                  defaultMessage="Mother Order"
                  description="mother order"
                />
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage
                  defaultMessage="Date Shipped"
                  description="date shipped"
                />
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage
                  defaultMessage="Date Completed"
                  description="date completed"
                />
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage
                  defaultMessage="Delivery Type"
                  description="delivery type"
                />
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage
                  defaultMessage="Amount"
                  description="amount"
                />
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                <FormattedMessage defaultMessage="Rider" description="rider" />
              </TableCellHeader>
              {forRemittance && (
                <TableCellHeader
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  <FormattedMessage
                    defaultMessage="Payment Collected"
                    description="payment collected"
                  />
                </TableCellHeader>
              )}
            </TableRow>
          </TableHead>

          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={forRemittance ? numberOfColumns + 1 : numberOfColumns}
                settings={settings}
                hasNextPage={
                  pageInfo && !disabled ? pageInfo.hasNextPage : false
                }
                onNextPage={onNextPage}
                onUpdateListSettings={onUpdateListSettings}
                hasPreviousPage={
                  pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                }
                onPreviousPage={onPreviousPage}
              />
            </TableRow>
          </TableFooter>

          <TableBody>
            <TableBodyContent
              tblClasses={tblClasses}
              numberOfColumns={
                forRemittance ? numberOfColumns + 1 : numberOfColumns
              }
              dateFormat={dateFormat}
              loadingOrders={loadingOrders}
              orderList={orderList}
              checkedOrders={checkedOrders}
              handleChecked={handleChecked}
              forRemittance={forRemittance}
              {...listProps}
            />
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default RemittanceRunnerTableToRemit;
